import * as React from 'react';

import moment from 'moment';

import {
    MONTHS_IN_YEAR,
    MONTHS_IN_QUARTER,
    type MonthEntry,
    type QuarterEntry,
    getCalendarYear,
    getMonthLabel,
    monthAndYearAreInCurrentFiscalYear,
    getFiscalYearStartMonth,
} from './helpers';
import {UserQuotaTableCell} from './user-quota-table-cell';

import styles from './user-quota-grid.module.css';

export type Props = {
    adjMonths: MonthEntry[];
    quarterValues: QuarterEntry[];
    fiscalYear: number;
    isEditing: boolean;
    calendarMonthToFocus: number | null;
    setIsEditing: (focusCalendarMonthFromCell: number) => void;
    onMonthValueChange: (calendarMonth: number, value: number) => void;
};

const currentYear = moment().year();
const currentMonth = moment().month() + 1;

export function UserQuotaGrid(props: Props) {
    const yearSuffix = String(props.fiscalYear % 100).padStart(2, '0');

    const autoFocusIndex = getAutoFocusIndex(
        props.calendarMonthToFocus,
        props.adjMonths,
        props.fiscalYear
    );

    const cells = [];
    for (let i = 0; i < MONTHS_IN_YEAR; i++) {
        const month = props.adjMonths[i];
        if (!month) {
            continue;
        }

        const calendarYear = getCalendarYear(
            month.month,
            props.fiscalYear,
            getFiscalYearStartMonth()
        );

        cells.push(
            <div key={month.month} className={styles.cell}>
                <UserQuotaTableCell
                    cellLabel={getMonthLabel(month.month, calendarYear, getFiscalYearStartMonth())}
                    value={month.value}
                    isEditing={props.isEditing}
                    onEdit={() => {
                        props.setIsEditing(month.month);
                    }}
                    isReadOnly={false}
                    onChange={
                        props.isEditing
                            ? (amount) => props.onMonthValueChange(month.month, amount)
                            : undefined
                    }
                    autoFocus={i === autoFocusIndex}
                />
            </div>
        );

        if ((i + 1) % MONTHS_IN_QUARTER === 0) {
            const quarter = Math.floor((i + 1) / MONTHS_IN_QUARTER);
            const q = props.quarterValues[quarter - 1];
            if (!q) {
                continue;
            }

            cells.push(
                <div key={`q-${q.quarter}`} className={styles['footer-cell']}>
                    <UserQuotaTableCell
                        cellLabel={`Q${q.quarter} ${yearSuffix}`}
                        value={q.value}
                        isEditing={false}
                        isReadOnly={true}
                    />
                </div>
            );
        }
    }

    return <div className={styles.grid}>{cells}</div>;
}

function getAutoFocusIndex(
    calendarMonthToFocus: number | null,
    adjMonths: MonthEntry[],
    fiscalYear: number
) {
    for (let i = 0; i < adjMonths.length; i++) {
        const month = adjMonths[i];
        if (!month) {
            continue;
        }

        if (calendarMonthToFocus !== null) {
            if (month.month === calendarMonthToFocus) {
                return i;
            }
            continue;
        }

        const calendarYear = getCalendarYear(month.month, fiscalYear, getFiscalYearStartMonth());
        const isMonthAndYearInCurrentFiscalYear = monthAndYearAreInCurrentFiscalYear(
            moment(),
            month.month,
            calendarYear,
            NutshellFrontendSettings.fiscal_year
        );

        if (
            isMonthAndYearInCurrentFiscalYear &&
            calendarYear === currentYear &&
            month.month === currentMonth
        ) {
            return i;
        }
    }

    return 0;
}
